import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import SignatureCanvas from 'react-signature-canvas';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  PenTool, Trash2, Check, X, Search, User, UserCheck, UserX,
  RotateCcw, ArrowRight, Shield, RefreshCw, Eye, Download, Clock
} from 'lucide-react';
import { Input } from '../../components/ui/input';

const DigitalSignaturesPage = () => {
  const { user, hasRole } = useAuth();
  const [signatures, setSignatures] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewFilter, setViewFilter] = useState('all');
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [signatureData, setSignatureData] = useState(null);
  const sigCanvasRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [previewSig, setPreviewSig] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [sigsRes, usersRes] = await Promise.all([
        api.get('/signatures'),
        api.get('/users')
      ]);
      setSignatures(sigsRes.data);
      setUsers(usersRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const usersWithSig = new Set(signatures.map(s => s.user_id));
  const activeUsers = users.filter(u => u.is_active !== false);
  const withSigCount = activeUsers.filter(u => usersWithSig.has(u.id)).length;
  const withoutSigCount = activeUsers.length - withSigCount;

  const getFilteredItems = () => {
    const q = searchTerm.toLowerCase();
    if (viewFilter === 'without') {
      return activeUsers.filter(u => !usersWithSig.has(u.id)).filter(u =>
        !q || (u.full_name || '').toLowerCase().includes(q)
      ).map(u => ({ type: 'user', ...u }));
    }
    let sigs = signatures;
    if (viewFilter === 'with') sigs = signatures;
    return sigs.filter(s => !q || (s.user_name || '').toLowerCase().includes(q) || (s.user_function || '').toLowerCase().includes(q))
      .map(s => ({ type: 'sig', ...s }));
  };

  const filteredItems = getFilteredItems();

  const handleOpenWizard = (userId = '') => {
    setSelectedUserId(userId);
    setWizardStep(userId ? 2 : 1);
    setSignatureData(null);
    setShowWizard(true);
  };

  const handleClearCanvas = () => {
    sigCanvasRef.current?.clear();
    setSignatureData(null);
  };

  const handleFinishSign = () => {
    if (sigCanvasRef.current?.isEmpty()) {
      toast.error('Semnati mai intai in casuta');
      return;
    }
    setSignatureData(sigCanvasRef.current.toDataURL('image/png'));
    setWizardStep(3);
  };

  const handleSave = async () => {
    if (!selectedUserId || !signatureData) return;
    try {
      setSaving(true);
      await api.post('/signatures', { user_id: selectedUserId, signature_data: signatureData });
      toast.success('Semnatura salvata cu succes');
      setShowWizard(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Eroare la salvare'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Sigur doriti stergerea semnaturii?')) return;
    try {
      await api.delete(`/signatures/${userId}`);
      toast.success('Semnatura stearsa');
      fetchData();
    } catch (e) { toast.error('Eroare la stergere'); }
  };

  const selectedUser = users.find(u => u.id === selectedUserId);

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col" data-testid="digital-signatures-page">
      {/* Header */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b bg-white flex-shrink-0">
        <div>
          <h1 className="text-base sm:text-lg font-bold text-slate-900">Semnaturi Digitale</h1>
          <p className="text-[11px] text-slate-500">Inregistrare, alocare si gestionare semnaturi tehnicieni</p>
        </div>
        {hasRole('admin', 'manager') && (
          <Button size="sm" className="gap-1.5 h-9" onClick={() => handleOpenWizard()} data-testid="add-signature-btn">
            <PenTool className="w-4 h-4" /> <span className="hidden sm:inline">Inregistreaza semnatura</span><span className="sm:hidden">Adauga</span>
          </Button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 space-y-4">

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-3">
            <button onClick={() => setViewFilter('all')}
              className={`rounded-xl border-2 p-3 sm:p-4 text-left transition-all ${viewFilter === 'all' ? 'border-blue-500 bg-blue-50 shadow-sm' : 'border-slate-200 hover:border-slate-300'}`}>
              <div className="flex items-center gap-2 mb-1.5">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${viewFilter === 'all' ? 'bg-blue-600' : 'bg-slate-200'}`}>
                  <User className={`w-4 h-4 ${viewFilter === 'all' ? 'text-white' : 'text-slate-500'}`} />
                </div>
                <span className="text-xs text-slate-500 hidden sm:block">Total utilizatori</span>
              </div>
              <span className="text-2xl font-bold text-slate-800">{activeUsers.length}</span>
            </button>
            <button onClick={() => setViewFilter('with')}
              className={`rounded-xl border-2 p-3 sm:p-4 text-left transition-all ${viewFilter === 'with' ? 'border-emerald-500 bg-emerald-50 shadow-sm' : 'border-slate-200 hover:border-slate-300'}`}>
              <div className="flex items-center gap-2 mb-1.5">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${viewFilter === 'with' ? 'bg-emerald-600' : 'bg-slate-200'}`}>
                  <UserCheck className={`w-4 h-4 ${viewFilter === 'with' ? 'text-white' : 'text-slate-500'}`} />
                </div>
                <span className="text-xs text-slate-500 hidden sm:block">Inregistrate</span>
              </div>
              <span className="text-2xl font-bold text-emerald-700">{withSigCount}</span>
            </button>
            <button onClick={() => setViewFilter('without')}
              className={`rounded-xl border-2 p-3 sm:p-4 text-left transition-all ${viewFilter === 'without' ? 'border-amber-500 bg-amber-50 shadow-sm' : 'border-slate-200 hover:border-slate-300'}`}>
              <div className="flex items-center gap-2 mb-1.5">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${viewFilter === 'without' ? 'bg-amber-600' : 'bg-slate-200'}`}>
                  <UserX className={`w-4 h-4 ${viewFilter === 'without' ? 'text-white' : 'text-slate-500'}`} />
                </div>
                <span className="text-xs text-slate-500 hidden sm:block">Neinregistrate</span>
              </div>
              <span className="text-2xl font-bold text-amber-700">{withoutSigCount}</span>
            </button>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <Input placeholder="Cauta dupa nume sau functie..." value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)} className="pl-9 h-10" data-testid="search-signatures" />
          </div>

          {/* Content */}
          {loading ? (
            <div className="text-center py-12 text-slate-400">Se incarca...</div>
          ) : viewFilter === 'without' ? (
            /* Users WITHOUT signature - table view */
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Badge className="bg-amber-100 text-amber-700 text-xs">{filteredItems.length} utilizatori fara semnatura</Badge>
              </div>
              {filteredItems.length === 0 ? (
                <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center">
                  <UserCheck className="w-10 h-10 mx-auto mb-2 text-emerald-400" />
                  <p className="text-sm font-medium text-slate-600">Toti utilizatorii au semnatura inregistrata</p>
                </div>
              ) : (
                <div className="border rounded-xl overflow-hidden divide-y">
                  {filteredItems.map(u => (
                    <div key={u.id} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-bold text-amber-700">{(u.full_name || '?')[0]}</span>
                        </div>
                        <div>
                          <span className="text-sm font-semibold text-slate-800">{u.full_name}</span>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-slate-500">{u.function || u.role}</span>
                            <Badge className="bg-amber-50 text-amber-600 text-[10px] px-1.5">Fara semnatura</Badge>
                          </div>
                        </div>
                      </div>
                      {hasRole('admin', 'manager') && (
                        <Button size="sm" className="gap-1 text-xs h-8" onClick={() => handleOpenWizard(u.id)} data-testid={`add-sig-${u.id}`}>
                          <PenTool className="w-3 h-3" /> Inregistreaza
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* Signatures grid/table */
            filteredItems.length === 0 ? (
              <div className="border-2 border-dashed border-slate-200 rounded-xl p-10 text-center">
                <PenTool className="w-12 h-12 mx-auto mb-3 text-slate-200" />
                <p className="text-base font-semibold text-slate-600">Nicio semnatura inregistrata</p>
                <p className="text-sm text-slate-400 mt-1">Inregistrati semnaturi pentru tehnicieni din butonul de mai sus</p>
              </div>
            ) : (
              <>
                {/* Desktop: Table view */}
                <div className="hidden md:block border rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 w-16">#</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Tehnician</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Functie</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500">Semnatura</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Actualizat</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">De catre</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 w-28">Actiuni</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {filteredItems.map((sig, idx) => (
                        <tr key={sig.user_id} className="hover:bg-slate-50 transition-colors" data-testid={`sig-row-${sig.user_id}`}>
                          <td className="px-4 py-3 text-slate-400 text-xs">{idx + 1}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2.5">
                              <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                <span className="text-sm font-bold text-blue-700">{(sig.user_name || '?')[0]}</span>
                              </div>
                              <span className="font-semibold text-slate-800">{sig.user_name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-slate-500 text-xs">{sig.user_function || sig.user_role}</td>
                          <td className="px-4 py-3">
                            <div className="h-12 w-32 mx-auto border rounded-lg bg-white flex items-center justify-center cursor-pointer hover:shadow-md transition-shadow"
                              onClick={() => setPreviewSig(sig)}>
                              {sig.signature_data ? (
                                <img src={sig.signature_data} alt="" className="max-h-full max-w-full object-contain p-1" />
                              ) : <span className="text-[10px] text-slate-300">—</span>}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-500">
                            <div className="flex items-center gap-1"><Clock className="w-3 h-3" />{sig.updated_at ? new Date(sig.updated_at).toLocaleDateString('ro-RO') : '—'}</div>
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-500">{sig.updated_by || '—'}</td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setPreviewSig(sig)} title="Previzualizare">
                                <Eye className="w-3.5 h-3.5 text-slate-400" />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => handleOpenWizard(sig.user_id)} title="Resemneaza" data-testid={`edit-sig-${sig.user_id}`}>
                                <RefreshCw className="w-3.5 h-3.5 text-blue-500" />
                              </Button>
                              {hasRole('admin', 'manager') && (
                                <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => handleDelete(sig.user_id)} title="Sterge" data-testid={`delete-sig-${sig.user_id}`}>
                                  <Trash2 className="w-3.5 h-3.5 text-red-400" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile: Card grid */}
                <div className="md:hidden grid gap-3 grid-cols-1 sm:grid-cols-2">
                  {filteredItems.map(sig => (
                    <Card key={sig.user_id} className="overflow-hidden hover:shadow-md transition-shadow">
                      <CardContent className="p-0">
                        <div className="flex items-center justify-between px-3 py-2.5 border-b bg-slate-50/50">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                              <span className="text-xs font-bold text-blue-700">{(sig.user_name || '?')[0]}</span>
                            </div>
                            <div>
                              <h3 className="font-semibold text-xs text-slate-800">{sig.user_name}</h3>
                              <p className="text-[10px] text-slate-500">{sig.user_function || sig.user_role}</p>
                            </div>
                          </div>
                          <div className="flex gap-0.5">
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleOpenWizard(sig.user_id)}>
                              <RefreshCw className="w-3 h-3 text-blue-500" />
                            </Button>
                            {hasRole('admin', 'manager') && (
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleDelete(sig.user_id)}>
                                <Trash2 className="w-3 h-3 text-red-400" />
                              </Button>
                            )}
                          </div>
                        </div>
                        <div className="p-3" onClick={() => setPreviewSig(sig)}>
                          <div className="border rounded-lg bg-white h-20 flex items-center justify-center relative cursor-pointer">
                            {sig.signature_data ? (
                              <img src={sig.signature_data} alt="" className="max-h-full max-w-full object-contain p-2" />
                            ) : <span className="text-[10px] text-slate-300">—</span>}
                            <div className="absolute bottom-2 left-4 right-4 border-t border-dashed border-slate-200" />
                          </div>
                          <p className="text-[10px] text-slate-400 mt-1.5 text-right">
                            {sig.updated_at ? new Date(sig.updated_at).toLocaleDateString('ro-RO') : ''}
                            {sig.updated_by ? ` · ${sig.updated_by}` : ''}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            )
          )}
        </div>
      </div>

      {/* === WIZARD Dialog === */}
      <Dialog open={showWizard} onOpenChange={setShowWizard}>
        <DialogContent className="max-w-md p-0 overflow-hidden">
          <div className="flex border-b">
            {[
              { n: 1, label: 'Selectare tehnician', icon: User },
              { n: 2, label: 'Captura semnatura', icon: PenTool },
              { n: 3, label: 'Verificare & salvare', icon: Shield },
            ].map((s) => (
              <div key={s.n} className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-medium transition-all ${
                wizardStep === s.n ? 'bg-blue-600 text-white' : wizardStep > s.n ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-400'
              }`}>
                {wizardStep > s.n ? <Check className="w-3.5 h-3.5" /> : <s.icon className="w-3.5 h-3.5" />}
                <span className="hidden sm:inline">{s.label}</span>
              </div>
            ))}
          </div>

          <div className="p-5">
            {wizardStep === 1 && (
              <div className="space-y-4" data-testid="wizard-step-1">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center mx-auto mb-3">
                    <User className="w-8 h-8 text-blue-600" />
                  </div>
                  <h2 className="text-lg font-bold text-slate-800">Selectati tehnicianul</h2>
                  <p className="text-xs text-slate-500 mt-1">Alegeti persoana careia ii inregistrati semnatura digitala</p>
                </div>
                <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                  <SelectTrigger className="h-11" data-testid="user-select"><SelectValue placeholder="Selectati utilizator..." /></SelectTrigger>
                  <SelectContent>
                    {activeUsers.map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{u.full_name}</span>
                          <span className="text-slate-400 text-xs">· {u.function || u.role}</span>
                          {usersWithSig.has(u.id) && <Badge className="bg-emerald-100 text-emerald-600 text-[10px] px-1 ml-1">are semnatura</Badge>}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button className="w-full h-11" disabled={!selectedUserId} onClick={() => setWizardStep(2)} data-testid="wizard-next-1">
                  Continua <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}

            {wizardStep === 2 && (
              <div className="space-y-4" data-testid="wizard-step-2">
                <div className="text-center">
                  <h2 className="text-lg font-bold text-slate-800">Captura semnatura</h2>
                  <p className="text-xs text-slate-500 mt-1">
                    <strong>{selectedUser?.full_name}</strong> — semnati cu degetul sau mouse-ul in casuta de mai jos
                  </p>
                </div>
                <div className="border-2 border-blue-300 rounded-xl bg-white relative shadow-inner" style={{ height: '200px' }}>
                  <SignatureCanvas ref={sigCanvasRef} penColor="#1a1a2e" minWidth={1.5} maxWidth={3.5}
                    canvasProps={{ className: 'rounded-xl', style: { width: '100%', height: '100%', borderRadius: '0.75rem', touchAction: 'none' } }}
                    backgroundColor="rgb(255,255,255)" />
                  <div className="absolute bottom-8 left-6 right-6 border-t border-dashed border-slate-300" />
                  <span className="absolute bottom-2 left-1/2 -translate-x-1/2 text-[10px] text-slate-400 bg-white px-2">Semnatura</span>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" className="text-xs gap-1" onClick={handleClearCanvas}>
                    <RotateCcw className="w-3 h-3" /> Sterge tot
                  </Button>
                  <div className="flex-1" />
                  <Button variant="outline" size="sm" onClick={() => setWizardStep(1)}>Inapoi</Button>
                  <Button size="sm" onClick={handleFinishSign} data-testid="wizard-finish-sign">
                    Verifica <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </div>
              </div>
            )}

            {wizardStep === 3 && (
              <div className="space-y-4" data-testid="wizard-step-3">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                    <Check className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h2 className="text-lg font-bold text-slate-800">Verificare finala</h2>
                  <p className="text-xs text-slate-500 mt-1">Confirmati datele inainte de salvare</p>
                </div>

                <div className="bg-slate-50 rounded-xl p-4 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-lg font-bold text-blue-600">{(selectedUser?.full_name || '?')[0]}</span>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-800">{selectedUser?.full_name}</p>
                    <p className="text-xs text-slate-500">{selectedUser?.function || selectedUser?.role}</p>
                  </div>
                  <Badge className="bg-blue-100 text-blue-700 ml-auto text-xs">
                    {usersWithSig.has(selectedUserId) ? 'Actualizare' : 'Noua'}
                  </Badge>
                </div>

                <div className="border-2 border-emerald-300 rounded-xl bg-white p-3 h-28 flex items-center justify-center relative">
                  {signatureData && <img src={signatureData} alt="" className="max-h-full max-w-full" />}
                  <div className="absolute bottom-4 left-6 right-6 border-t border-dashed border-slate-200" />
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1 gap-1" onClick={() => { setWizardStep(2); setSignatureData(null); }}>
                    <RotateCcw className="w-3.5 h-3.5" /> Resemneaza
                  </Button>
                  <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 gap-1" onClick={handleSave} disabled={saving} data-testid="wizard-save">
                    <Check className="w-3.5 h-3.5" /> {saving ? 'Se salveaza...' : 'Salveaza semnatura'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* === Preview Dialog === */}
      <Dialog open={!!previewSig} onOpenChange={(v) => { if (!v) setPreviewSig(null); }}>
        <DialogContent className="max-w-sm p-0 overflow-hidden">
          {previewSig && (
            <>
              <DialogHeader className="px-4 py-3 border-b bg-slate-50">
                <DialogTitle className="text-sm font-bold flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <span className="text-xs font-bold text-blue-700">{(previewSig.user_name || '?')[0]}</span>
                  </div>
                  <div>
                    <div>{previewSig.user_name}</div>
                    <div className="text-[11px] text-slate-400 font-normal">{previewSig.user_function || previewSig.user_role}</div>
                  </div>
                </DialogTitle>
              </DialogHeader>
              <div className="p-5">
                <div className="border-2 border-slate-200 rounded-xl bg-white p-4 h-36 flex items-center justify-center relative">
                  {previewSig.signature_data ? (
                    <img src={previewSig.signature_data} alt="" className="max-h-full max-w-full object-contain" />
                  ) : <span className="text-sm text-slate-300">Fara semnatura</span>}
                  <div className="absolute bottom-5 left-6 right-6 border-t border-dashed border-slate-300" />
                </div>
                <div className="text-xs text-slate-400 mt-3 space-y-0.5">
                  <div>Inregistrat: {previewSig.created_at ? new Date(previewSig.created_at).toLocaleString('ro-RO') : '—'}</div>
                  <div>Actualizat: {previewSig.updated_at ? new Date(previewSig.updated_at).toLocaleString('ro-RO') : '—'}</div>
                  <div>De catre: {previewSig.updated_by || '—'}</div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" className="flex-1 gap-1 text-xs" onClick={() => { setPreviewSig(null); handleOpenWizard(previewSig.user_id); }}>
                    <RefreshCw className="w-3 h-3" /> Actualizeaza
                  </Button>
                  {hasRole('admin', 'manager') && (
                    <Button variant="outline" className="gap-1 text-xs text-red-500" onClick={() => { handleDelete(previewSig.user_id); setPreviewSig(null); }}>
                      <Trash2 className="w-3 h-3" /> Sterge
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DigitalSignaturesPage;
